import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import {
    addEntities,
    getActiveEntity,
    getEntity,
    resetActiveId,
    selectActiveEntity,
    selectAllEntities,
    setActiveId,
    setEntities,
    updateEntities,
    withActiveId,
    withEntities,
} from '@ngneat/elf-entities';
import { CpwTask } from '../models/task.model';
import { TaskFilterEvent, TaskFilters } from '../models/task-filters';
import { cloneDeep } from 'lodash-es';

export interface TasksState {
    taskFilters: TaskFilters;
}

@Injectable({ providedIn: 'root' })
export class CpwTaskRepository {
    private store = createStore(
        { name: 'cpw-tasks' },
        withProps<TasksState>({
            taskFilters: {
                zones: [],
                equipmentTypes: [],
            },
        }),
        withEntities<CpwTask>(),
        withActiveId()
    );

    tasks$ = this.store.pipe(selectAllEntities());
    activeTask$ = this.store.pipe(selectActiveEntity());
    taskFilters$ = this.store.pipe(select(state => state.taskFilters));

    get activeTask(): CpwTask | undefined {
        return this.store.query(getActiveEntity());
    }

    setTasks(tasks: CpwTask[]): void {
        const claimedActiveTask = this.activeTask?.claimedAt
            ? cloneDeep(this.activeTask)
            : null;
        this.store.update(setEntities(tasks));
        if (claimedActiveTask) {
            const replacement = this.store.query(
                getEntity(claimedActiveTask.id)
            );
            if (!replacement) {
                this.store.update(addEntities(claimedActiveTask));
            } else if (!replacement.claimedAt) {
                this.store.update(
                    updateEntities(claimedActiveTask.id, {
                        claimedAt: claimedActiveTask.claimedAt,
                    })
                );
            }
        }
    }

    setActiveTask(taskId: string) {
        this.store.update(setActiveId(taskId));
    }

    clearTasks() {
        this.store.reset();
    }

    updateTask(task: CpwTask): void {
        this.store.update(updateEntities(task.id, cloneDeep(task)));
    }

    clearActiveTask() {
        this.store.update(resetActiveId());
    }

    getTaskFilters() {
        return this.store.query(state => state.taskFilters);
    }

    updateTaskFilter(filter: TaskFilterEvent) {
        this.store.update(state => {
            return {
                ...state,
                taskFilters: {
                    ...state.taskFilters,
                    [filter.filter]: filter.value,
                },
            };
        });
    }

    clearTaskFilters() {
        this.store.update(state => {
            return {
                ...state,
                taskFilters: {
                    zones: [],
                    equipmentTypes: [],
                },
            };
        });
    }

    getTask(taskId: string) {
        return this.store.query(getEntity(taskId));
    }
}
